import React, { useState, useEffect } from 'react';
import { Button, FormHelperText } from '@mui/material';
import { withRouter } from 'react-router-dom';
import { copyObject, _t, isNullOrUndefined } from 'utils';
import { validateForm } from 'utils/form-validation';
import { VALIDATION } from 'constant';
import Select from './select-field';
import Range from './range-slider';
import Input from './input-field';
import Password from './password-field';
import NumberInput from './input-number-field';
import Switch from './switch';
import ImageSelector from './image-selector';
import UserSelector from './user-selector';
import ResourceSelector from './resource-selector';
import GroupSelector from './group-selector';
import BootDeviceSelector from './boot-device-selector';
import TimePicker from './time-picker';
import IPSelector from './ip-selector';
import IPSSelector from './ips-selector';
import TimeRangeSelector from './time-range-selector';
import NicSelector from './nic-selector';
import LanSelector from './lan-selector';
import LocationSelector from './location-selector';
import VolumeSelector from './volume-selector';
import PortSelector from './port-selector';
import RangeBasic from './range-basic-selector';
import LansSelector from './lans-selector';
import ServerSelector from './server-selector';
import MonitoringActionSelector from './monitoring-action-selector';
import DataCenterSelector from './datacenter-selector';
import IconPopup from '../icon-popup';
import ArrayField from './array-field';
import ObjectField from './object-field';
import AutoCompleteInput from './autocomplete-field';
import { UserDataField } from './user-data-field';
import FileReaderInput from './file-reader';
import CommonSelector from './common-selector';
import DateTimePicker from './date-time-picker';
import MobileDateTimePicker from './mobile-date-time-picker';

const Form = ({
  title: formTitle = '',
  fields: fieldsProps = [],
  fieldsValues: fieldsValuesProp = {},
  submitTitle = _t('Save'),
  cancelTitle = _t('Close'),

  validations = [],
  validationErrors = null,

  isCancel = true,
  isActionButtons = true,

  onCancel = null,
  onSubmit = null,
  onChange: onChangeForm = null,
  onProps = null, // fieldsValues with updated values

  changeFields = [], // [{name = '', props = {}}]

  style = {},
  history = {},

  onlyFieldsValue = false,
  className = '',

  children,
  isChild,
  refresh,
  submitDisabled,
  validateOnBlur,
  customValidation,
}) => {
  const [fieldsValues, setFieldsValues] = useState(fieldsValuesProp || {});
  const [errors, setErrors] = useState(validationErrors || {});
  const [fields, setFields] = useState(fieldsProps);

  useEffect(() => {
    if (validationErrors) {
      setErrors(validationErrors);
    }
  }, [validationErrors]);

  useEffect(() => {
    if (onProps) {
      setFieldsValues(onProps);
    }
  }, [onProps]);

  useEffect(() => {
    if (changeFields.length) {
      changeFields.forEach(({ name: nameToChange, props: newProps }) => {
        const fieldIndex = fields.findIndex(({ name }) => name === nameToChange);
        if (fieldIndex !== -1) {
          fields[fieldIndex] = { ...fields[fieldIndex], ...newProps };
          const $errors = validateForm({
            fields: fields.filter((item) => item.name === nameToChange),
            fieldsValues: {
              [nameToChange]: (newProps && newProps.value) ? (newProps.value) : fieldsValues[nameToChange],
            },
            validations,
          });
          if (errors[nameToChange]) {
            const newErrors = { ...errors };
            if ($errors[nameToChange]) {
              newErrors[nameToChange] = $errors[nameToChange];
            } else {
              delete newErrors[nameToChange];
            }
            setErrors({ ...newErrors });
          }
        }
      });

      setFields([...fields]);
    }
    // eslint-disable-next-line
  }, [changeFields]);

  const onChange = ({ name, onChangeCallBack, value }) => {
    const { isNumber = false, setValueFrom } = fieldsProps.find((e) => e.name === name) || {};

    let newValue = isNumber ? parseFloat(value) : value;
    if (setValueFrom) newValue = setValueFrom(newValue, fieldsValues, fields);
    const newFieldValues = { ...fieldsValues, [name]: newValue };

    setFieldsValues(newFieldValues);
    if (onChangeCallBack) onChangeCallBack(newValue);
    if (onChangeForm) onChangeForm(newFieldValues);
  };

  const handlePropagateValidation = (subFormValues, subFormName) => {
    const fieldValues = {
      [subFormName]: {
        ...subFormValues,
      },
    };
    const $errors = validateForm({
      fields: [fields.filter((item) => item.name === subFormName)[0]],
      fieldsValues: fieldValues,
      validations: [
        ...validations,
      ],
    });
    if ($errors[subFormName]) {
      setErrors({ ...errors, ...$errors });
    } else {
      const tempErrors = copyObject(errors);
      delete tempErrors[subFormName];
      setErrors(tempErrors);
    }
  };

  const onBlur = (e) => {
    if (validateOnBlur) {
      const { name, value } = e.target;
      const $errors = validateForm({ fields: fields.filter((item) => item.name === name), fieldsValues: { [name]: value }, validations });

      if ($errors[name]) {
        setErrors({ ...errors, ...$errors });
      } else {
        const tempErrors = copyObject(errors);
        delete tempErrors[name];
        setErrors(tempErrors);
      }
    }
  };
  const onSubmitClick = () => {
    if (!onSubmit) return;

    let $errors = validateForm({ fields, fieldsValues, validations });

    if (customValidation) {
      const customErrors = customValidation(fieldsValues);
      if (customErrors) {
        $errors = { ...$errors, ...customErrors };
      }
    }

    if (!Object.keys($errors).length) {
      const fieldsValuesToPass = copyObject(fieldsValues);
      // eslint-disable-next-line max-len
      const excludeFieldsNames = fields.filter(({ setExcludeValue }) => (setExcludeValue && setExcludeValue(fieldsValues))).map(({ name }) => name) || [];
      excludeFieldsNames.forEach((e) => delete fieldsValuesToPass[e]);

      if (onlyFieldsValue) {
        const fieldNames = fields.map((e) => e.name);
        Object.keys(fieldsValuesToPass).forEach((e) => {
          if (!fieldNames.includes(e)) delete fieldsValuesToPass[e];
        });
      }

      onSubmit(fieldsValuesToPass);
      setErrors({});
    } else {
      setErrors($errors);
      setTimeout(() => {
        const errorElem = document.querySelector('.form .Mui-error');
        if (errorElem) {
          errorElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const onCancelClick = () => (onCancel ? onCancel() : history.goBack());

  return (
    <div className={`form ${className}`} style={style}>
      {formTitle && <h3>{formTitle}</h3>}
      {fields.map((field) => {
        const {
          name = '',
          type = 'text',
          title: _title,
          setTitle,
          options = [],
          range = [],
          onChange: onChangeCallBack = null,
          style: styleField = {},
          helpText = '',
          fields: arrayFields = [],
          validations: arrayValidations = [],
          setVisibility,
          visible: visibility = true,
          isNumber,
          updateProps,
          setValueFrom,
          setValueTo,
          setExcludeValue,
          externalError,
          onBlur: onBlurField,
          ...restProps
        } = field;
        let visible = visibility;
        if (setVisibility) visible = setVisibility(fieldsValues, fields);

        const props = {
          ...restProps,
          ...(updateProps ? updateProps(fieldsValues, fields) : {}),
          name,
          onBlur: (e) => {
            onBlur(e);
            if (onBlurField) onBlurField(e);
          },
        };

        if (props.hidden) return <span key={name} />;

        const fieldValue = setValueTo ? setValueTo(fieldsValues[name], fieldsValues, fields) : fieldsValues[name];
        let title = _title || (setTitle && setTitle(fieldsValues, fields)) || '';
        title = validations.some((e) => e.name === name && e.type === VALIDATION.REQUIRED) ? `${title} * ` : title;

        if (type === 'dropdown') {
          return (visible && (
            <Select
              key={name}
              value={fieldValue || ''}
              onChange={(e) => onChange({ name, value: e.target.value, onChangeCallBack })}
              label={title}
              values={options}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'range' || type === 'slider') {
          return (visible && (
            <Range
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              label={title}
              value={fieldValue}
              range={range}
              options={options}
              helpText={helpText}
              error={externalError || errors[name] || props.error}
              {...props}
            />
          ));
        }

        if (type === 'switch') {
          return (visible && (
            <React.Fragment key={name}>
              <div className="form-swtich">
                <span>{title}</span>
                <Switch
                  checked={fieldValue || false}
                  onChange={(value) => onChange({ name, value, onChangeCallBack })}
                />
              </div>
              {(!!helpText) && <FormHelperText style={{ marginLeft: 0, marginTop: -5, marginBottom: 10 }}>{helpText}</FormHelperText>}
            </React.Fragment>
          ));
        }

        if (type === 'array') {
          return (visible && (
            <ArrayField
              key={name}
              value={fieldValue || []}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              fields={arrayFields}
              validations={arrayValidations}
              title={title}
              error={externalError || errors[name]}
              onProps={fieldsValuesProp[name]}
              changeFields={changeFields.filter((item) => !fields.find((f) => item.name === f.name))}
              isChild={isChild}
              refresh={refresh}
              validateOnBlur={validateOnBlur}
              propagateValidation={(e) => handlePropagateValidation(e, name)}
              {...props}
            />
          ));
        }

        if (type === 'object') {
          return (visible && (
            <ObjectField
              key={name}
              value={fieldValue || []}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              fields={arrayFields}
              validations={arrayValidations}
              title={title}
              error={externalError || errors[name]}
              onProps={fieldsValuesProp[name]}
              fieldsValues={fieldsValuesProp[name]}
              changeFields={changeFields.filter((item) => !fields.find((f) => item.name === f.name))}
              validateOnBlur={validateOnBlur}
              {...props}
            />
          ));
        }

        if (type === 'user-data') {
          return (visible && (
            <UserDataField
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              name={name}
            />
          ));
        }

        if (type === 'text') {
          return (visible && (
            <Input
              key={name}
              data-name={name}
              label={title}
              value={!isNullOrUndefined(fieldValue) ? fieldValue : ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              type={type}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'password') {
          return (visible && (
            <Password
              key={name}
              data-name={name}
              label={title}
              value={!isNullOrUndefined(fieldValue) ? fieldValue : ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'number') {
          return (visible && (
            <NumberInput
              key={name}
              data-name={name}
              label={title}
              value={!isNullOrUndefined(fieldValue) ? fieldValue : ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              type={type}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'autocomplete') {
          return (visible && (
            <AutoCompleteInput
              key={name}
              data-name={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              type={type}
              error={externalError || errors[name]}
              helpText={helpText}
              options={options}
              {...props}
            />
          ));
        }

        if (type === 'ip') {
          return (visible && (
            <IPSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              helpText={helpText}
              error={externalError || errors[name]}
              {...props}
            />
          ));
        }

        if (type === 'range-basic') {
          return (visible && (
            <RangeBasic
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'port') {
          return (visible && (
            <PortSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'time-range') {
          return (visible && (
            <TimeRangeSelector
              key={name}
              value={fieldValue}
              onChange={(value) => {
                onChange({
                  name,
                  value,
                  onChangeCallBack,
                });
              }}
            />
          ));
        }

        if (type === 'ips') {
          return (visible && (
            <IPSSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'lans') {
          return (visible && (
            <LansSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'location') {
          return (visible && (
            <LocationSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'nic') {
          return (visible && (
            <NicSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'lan') {
          return (visible && (
            <LanSelector
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'textarea') {
          return (visible && (
            <Input
              key={name}
              label={title}
              value={fieldValue || ''}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              multiline
              maxRows="3"
              minRows="3"
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'cdrom' || type === 'hdd') {
          return (visible && (
            <ImageSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              type={type.toUpperCase()}
              error={externalError || errors[name]}
              helpText={helpText}
              {...props}
            />
          ));
        }

        if (type === 'datacenter') {
          return (visible && (
            <DataCenterSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              type={type.toUpperCase()}
              error={externalError || errors[name]}
              {...props}
            />
          ));
        }

        if (type === 'user') {
          return (visible && (
            <UserSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
            />
          ));
        }

        if (type === 'volume') {
          return (visible && (
            <VolumeSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              error={externalError || errors[name]}
            />
          ));
        }

        if (type === 'boot') {
          return (visible && (
            <BootDeviceSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              devices={options}
              error={externalError || errors[name]}
            />
          ));
        }

        if (type === 'resource') {
          return (visible && (
            <ResourceSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              type={type.toUpperCase()}
            />
          ));
        }

        if (type === 'group') {
          return (visible && (
            <GroupSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              type={type.toUpperCase()}
            />
          ));
        }

        if (type === 'time') {
          return (visible && (
            <TimePicker
              key={name}
              label={title}
              value={fieldValue}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
            />
          ));
        }

        if (type === 'date-time') {
          return (visible && (
            <DateTimePicker
              key={name}
              label={title}
              value={fieldValue}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              minDateTime={restProps.minDateTime}
              maxDateTime={restProps.maxDateTime}
              disabled={restProps.disabled}
              clearable={restProps.clearable}
              helpText={helpText}
              error={externalError || errors[name]}
            />
          ));
        }

        if (type === 'mobile-date-time') {
          return (visible && (
            <MobileDateTimePicker
              key={name}
              label={title}
              value={fieldValue}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              minDateTime={restProps.minDateTime}
              maxDateTime={restProps.maxDateTime}
              disabled={restProps.disabled}
              clearable={restProps.clearable}
              clearSeconds={restProps.clearSeconds}
              helpText={helpText}
              error={externalError || errors[name]}
            />
          ));
        }

        if (type === 'label') {
          return (visible && (
            <p
              key={name}
              style={{
                margin: '0 0 8px 0',
                fontSize: 18,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                ...styleField,
              }}
            >
              <span>{title}</span>
              {helpText && (
                <span style={{ color: '#848484', cursor: 'help' }}>
                  <IconPopup
                    content={helpText}
                    outerStyle={{
                      width: '80%', minWidth: 350, maxWidth: 600, marginTop: 5, zIndex: 9999,
                    }}
                    innerStyle={{ width: 'calc(100% - 30px)', color: '#555', lineHeight: '1.4' }}
                  />
                </span>
              )}
            </p>
          ));
        }

        if (type === 'toggle') {
          return (visible && (
            <p
              key={name}
              style={{
                margin: '0 0 8px 0',
                fontSize: 18,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                ...styleField,
              }}
            >
              <span onClick={() => onChange({ name, value: !fieldValue, onChangeCallBack })}>{title}</span>
              {helpText && (
                <span style={{ color: '#848484', cursor: 'pointer' }}>
                  <IconPopup
                    content={helpText}
                    outerStyle={{
                      width: '80%', minWidth: 350, maxWidth: 600, marginTop: 5, zIndex: 9999,
                    }}
                    innerStyle={{ width: 'calc(100% - 30px)', color: '#555', lineHeight: '1.4' }}
                  />
                </span>
              )}
            </p>
          ));
        }

        if (type === 'server') {
          return (visible && (
            <ServerSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              error={externalError || errors[name]}
              {...props}
            />
          ));
        }

        if (type === 'monitor-action') {
          return (visible && (
            <MonitoringActionSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              error={externalError || errors[name]}
            />
          ));
        }

        if (type === 'file-reader') {
          return (visible && (
            <FileReaderInput
              key={name}
              name={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              error={externalError || errors[name]}
              title={title}
            />
          ));
        }

        if (type === 'common-selector') {
          return (visible && (
            <CommonSelector
              key={name}
              onChange={(value) => onChange({ name, value, onChangeCallBack })}
              value={fieldValue}
              error={externalError || errors[name]}
              title={title}
              helpText={helpText}
              {...props}
            />
          ));
        }

        return <div key={name} />;
      })}

      {children}

      {isActionButtons && (
        <div className="form-footer">
          {isCancel && <Button variant="contained" size="small" onClick={onCancelClick}>{cancelTitle}</Button>}
          {onSubmit && <Button disabled={submitDisabled} variant="contained" size="small" color="primary" onClick={onSubmitClick}>{submitTitle}</Button>}
        </div>
      )}
    </div>
  );
};

export default withRouter(Form);
